import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Search, Ban } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { AdminShell } from './admin-shell';

interface UserRow {
  userId: string; email: string; displayName: string | null;
  plan: string | null; authProvider: string | null;
  suspendedAt: string | null; createdAt: string;
  orgCount: number;
}

export function AdminUsersPage() {
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'users', search],
    queryFn: () => apiClient.adminListUsers(search || undefined),
  });
  const users = (data?.data as UserRow[] | undefined) ?? [];

  return (
    <AdminShell subtitle="All platform users. Click a user to inspect or take action.">

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Users ({users.length})</h1>
        </div>
        <form
          onSubmit={(e) => { e.preventDefault(); setSearch(q.trim()); }}
          className="flex items-center gap-2"
        >
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search email or name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-8 w-72"
            />
          </div>
        </form>
      </div>

      <Card className="p-0 overflow-hidden">
        <div className="divide-y divide-border">
          {isLoading && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading…</div>
          )}
          {!isLoading && users.length === 0 && (
            <div className="px-5 py-8 text-center text-sm text-muted-foreground">No users found.</div>
          )}
          {users.map((u) => (
            <Link key={u.userId} to={`/admin/users/${u.userId}`}
              className="px-5 py-3 flex items-center justify-between gap-3 hover:bg-muted/40 transition-colors">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-foreground truncate flex items-center gap-2">
                  {u.displayName ?? u.email}
                  {u.suspendedAt && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 inline-flex items-center gap-1">
                      <Ban className="h-3 w-3" /> suspended
                    </span>
                  )}
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {u.email} · {u.orgCount} {u.orgCount === 1 ? 'org' : 'orgs'} · plan: {u.plan ?? 'free'}
                </p>
              </div>
              <span className="text-xs text-muted-foreground shrink-0 font-mono">{u.userId.slice(0, 10)}…</span>
            </Link>
          ))}
        </div>
      </Card>
    </AdminShell>
  );
}
